/**
 * Discipline Configuration Index
 * 
 * Central registry for all discipline configurations.
 * Each discipline defines its own columns, calculations, API endpoints, etc.
 */

import { pipingConfig } from './piping';
import { pipingNdeConfig } from './pipingNde';
import { pipingTestingConfig } from './pipingTesting';
import { hvacConfig } from './hvac';
import { structuralConfig } from './structural';
import { mechanicalConfig } from './mechanical';
import { ductsConfig } from './ducts';
import { vesselsConfig } from './vessels';
import { chutesConfig } from './chutes';
import { conveyorsConfig } from './conveyors';
import { skidsConfig } from './skids';
import { civilConfig } from './civil';
import { electricalConfig } from './electrical';
import { boilermakerConfig } from './boilermaker';

/**
 * Map of discipline IDs to their configurations
 */
const disciplineConfigs = {
  // Piping & Insulation
  'piping': pipingConfig,
  'piping-insulation': pipingConfig, // Alias for menu compatibility
  
  // Piping NDE Tracker
  'piping-nde': pipingNdeConfig,

  // Piping Testing Tracker
  'piping-testing': pipingTestingConfig,
  
  // HVAC
  'hvac': hvacConfig,
  
  // Structural Steel
  'structural': structuralConfig,
  'structure': structuralConfig, // Alias for menu compatibility
  
  // Mechanical (Rotating Equipment)
  'mechanical': mechanicalConfig,
  'mechanical-rotating': mechanicalConfig, // Alias
  'mechanical-rotating-eqp': mechanicalConfig, // Alias for menu compatibility
  
  // Ducts
  'ducts': ductsConfig,
  'mechanical-ducts': ductsConfig, // Alias for menu compatibility
  
  // Vessels
  'vessels': vesselsConfig,
  'mechanical-vessels': vesselsConfig, // Alias for menu compatibility
  
  // Chutes
  'chutes': chutesConfig,
  'mechanical-chutes': chutesConfig, // Alias for menu compatibility
  
  // Conveyors
  'conveyors': conveyorsConfig,
  'mechanical-conveyors': conveyorsConfig, // Alias for menu compatibility
  
  // Skids
  'skids': skidsConfig,
  'mechanical-skids': skidsConfig, // Alias for menu compatibility
  
  // Civil
  'civil': civilConfig,
  
  // Electrical
  'electrical': electricalConfig,

  // Boilermaker (BMT / Field Tank)
  'boilermaker': boilermakerConfig,
};

/**
 * Universal tagging — map each discipline config to its backend tag entity
 * type (see src/config/tagEntityTypes.js on the backend). Decorating here
 * (instead of in every config file) gives all trackers a Tags column and
 * enables tag features in DisciplineMatrix in one place. NDE / Pipe Testing
 * sub-trackers are intentionally excluded for now.
 */
const TAG_ENTITY_TYPE_BY_CONFIG_ID = {
  'piping': 'tracker_piping',
  'hvac': 'hvac_tracking',
  'structural': 'structural_steel_tracking',
  'mechanical': 'rotating_equipment_tracking',
  'ducts': 'ducts_tracking',
  'vessels': 'vessels_tracking',
  'chutes': 'chutes_tracking',
  'conveyors': 'conveyors_tracking',
  'skids': 'skids_tracking',
  'civil': 'civil_tracking',
  'electrical': 'electrical_tracking',
  'boilermaker': 'boilermaker_tracking',
};

for (const config of new Set(Object.values(disciplineConfigs))) {
  const tagEntityType = TAG_ENTITY_TYPE_BY_CONFIG_ID[config.id];
  if (!tagEntityType) continue;
  config.tagEntityType = tagEntityType;
  if (!config.columns.some((c) => c.id === 'tags')) {
    config.columns.push({
      id: 'tags',
      header: 'Tags',
      accessor: 'tags',
      width: 150,
      editable: false,
      filterable: false,
      type: 'tags',
      showInDashboard: true,
    });
  }
}

/**
 * Get configuration for a specific discipline
 * @param {string} disciplineId - The discipline identifier
 * @returns {Object} The discipline configuration
 * @throws {Error} If discipline is not found
 */
export const getDisciplineConfig = (disciplineId) => {
  const config = disciplineConfigs[disciplineId];
  if (!config) {
    throw new Error(`Unknown discipline: ${disciplineId}. Available disciplines: ${Object.keys(disciplineConfigs).join(', ')}`);
  }
  return config;
};

/**
 * Check if a discipline exists
 * @param {string} disciplineId - The discipline identifier
 * @returns {boolean}
 */
export const hasDiscipline = (disciplineId) => {
  return disciplineId in disciplineConfigs;
};

/**
 * Get list of all available discipline IDs
 * @returns {string[]}
 */
export const getAvailableDisciplines = () => {
  // Return unique discipline IDs (excluding aliases)
  const unique = new Set();
  Object.values(disciplineConfigs).forEach(config => {
    unique.add(config.id);
  });
  return Array.from(unique);
};

/**
 * Get list of all discipline configurations
 * @returns {Object[]}
 */
export const getAllDisciplineConfigs = () => {
  // Return unique configs (excluding aliases)
  const seen = new Set();
  return Object.values(disciplineConfigs).filter(config => {
    if (seen.has(config.id)) return false;
    seen.add(config.id);
    return true;
  });
};

/**
 * Map menu item IDs to discipline IDs
 * Used for routing from ProjectMenu
 */
export const menuToDisciplineMap = {
  // Piping & Insulation
  'matrix': 'piping',
  'upload-matrix': 'piping',
  'export-excel': 'piping',
  'column-config': 'piping',
  'piping-insulation-rules-of-credit': 'piping',
  
  // HVAC (future)
  'hvac-progress-tracker-update': 'hvac',
  'hvac-upload-tracker': 'hvac',
  'hvac-export-excel': 'hvac',
  'hvac-column-config': 'hvac',
  'hvac-rules-of-credit': 'hvac',
  
  // Structural (future)
  'structure-progress-tracker-update': 'structural',
  'structure-upload-tracker': 'structural',
  'structure-export-excel': 'structural',
  'structure-column-config': 'structural',
  'structure-rules-of-credit': 'structural',
  
  // Mechanical - Rotating Equipment (future)
  'mechanical-rotating-eqp-progress-tracker-update': 'mechanical-rotating',
  'mechanical-rotating-eqp-upload-tracker': 'mechanical-rotating',
  'mechanical-rotating-eqp-export-excel': 'mechanical-rotating',
  'mechanical-rotating-eqp-column-config': 'mechanical-rotating',
  'mechanical-rotating-eqp-rules-of-credit': 'mechanical-rotating',
  
  // Ducts (future)
  'mechanical-ducts-progress-tracker-update': 'mechanical-ducts',
  'mechanical-ducts-upload-tracker': 'mechanical-ducts',
  'mechanical-ducts-export-excel': 'mechanical-ducts',
  'mechanical-ducts-column-config': 'mechanical-ducts',
  'mechanical-ducts-rules-of-credit': 'mechanical-ducts',
  
  // Vessels (future)
  'mechanical-vessels-progress-tracker-update': 'mechanical-vessels',
  'mechanical-vessels-upload-tracker': 'mechanical-vessels',
  'mechanical-vessels-export-excel': 'mechanical-vessels',
  'mechanical-vessels-column-config': 'mechanical-vessels',
  'mechanical-vessels-rules-of-credit': 'mechanical-vessels',
  
  // Chutes (future)
  'mechanical-chutes-progress-tracker-update': 'mechanical-chutes',
  'mechanical-chutes-upload-tracker': 'mechanical-chutes',
  'mechanical-chutes-export-excel': 'mechanical-chutes',
  'mechanical-chutes-column-config': 'mechanical-chutes',
  'mechanical-chutes-rules-of-credit': 'mechanical-chutes',
  
  // Conveyors (future)
  'mechanical-conveyors-progress-tracker-update': 'mechanical-conveyors',
  'mechanical-conveyors-upload-tracker': 'mechanical-conveyors',
  'mechanical-conveyors-export-excel': 'mechanical-conveyors',
  'mechanical-conveyors-column-config': 'mechanical-conveyors',
  'mechanical-conveyors-rules-of-credit': 'mechanical-conveyors',
  
  // Skids (future)
  'mechanical-skids-progress-tracker-update': 'mechanical-skids',
  'mechanical-skids-upload-tracker': 'mechanical-skids',
  'mechanical-skids-export-excel': 'mechanical-skids',
  'mechanical-skids-column-config': 'mechanical-skids',
  'mechanical-skids-rules-of-credit': 'mechanical-skids',
  
  // Civil (future)
  'civil-progress-tracker-update': 'civil',
  'civil-upload-tracker': 'civil',
  'civil-export-excel': 'civil',
  'civil-column-config': 'civil',
  'civil-rules-of-credit': 'civil',
  
  // Electrical (future)
  'electrical-progress-tracker-update': 'electrical',
  'electrical-upload-tracker': 'electrical',
  'electrical-export-excel': 'electrical',
  'electrical-column-config': 'electrical',
  'electrical-rules-of-credit': 'electrical',

  // Boilermaker
  'boilermaker-progress-tracker-update': 'boilermaker',
  'boilermaker-upload-tracker': 'boilermaker',
  'boilermaker-export-excel': 'boilermaker',
  'boilermaker-column-config': 'boilermaker',
  'boilermaker-rules-of-credit': 'boilermaker',
};

/**
 * Get discipline ID from menu item ID
 * @param {string} menuItemId - The menu item identifier
 * @returns {string|null} The discipline ID or null if not found
 */
export const getDisciplineFromMenuItem = (menuItemId) => {
  return menuToDisciplineMap[menuItemId] || null;
};

// Re-export individual configs for direct import if needed
export { pipingConfig } from './piping';
export { pipingNdeConfig } from './pipingNde';
export { pipingTestingConfig } from './pipingTesting';
export { hvacConfig } from './hvac';
export { structuralConfig } from './structural';
export { mechanicalConfig } from './mechanical';
export { ductsConfig } from './ducts';
export { vesselsConfig } from './vessels';
export { chutesConfig } from './chutes';
export { conveyorsConfig } from './conveyors';
export { skidsConfig } from './skids';
export { civilConfig } from './civil';
export { electricalConfig } from './electrical';
export { boilermakerConfig } from './boilermaker';

















